import {
    REDUX_BROADCAST_CHANNEL,
    reduxBroadcastActionTypes,
} from '@/app/store/stateBroadcast/stateBroadcastConfig.ts';

import type { Middleware } from '@reduxjs/toolkit';

const reduxBroadcastActionTypeSet = new Set<string>(reduxBroadcastActionTypes);

type ReduxBroadcastMessage = {
    type: string;
    payload?: unknown;
};

let channel: BroadcastChannel | null = null;
let isRemoteDispatchInProgress = false;

const getChannel = (): BroadcastChannel | null => {
    if (typeof BroadcastChannel === 'undefined') return null;

    if (!channel) {
        channel = new BroadcastChannel(REDUX_BROADCAST_CHANNEL);
    }

    return channel;
};

export const stateBroadcast: Middleware = () => (next) => (action) => {
    const result = next(action);

    if (isRemoteDispatchInProgress) {
        return result;
    }

    if (!action || typeof action !== 'object' || !('type' in action)) {
        return result;
    }

    if (typeof action.type !== 'string' || !reduxBroadcastActionTypeSet.has(action.type)) {
        return result;
    }

    getChannel()?.postMessage({
        type: action.type,
        payload: 'payload' in action ? action.payload : undefined,
    } satisfies ReduxBroadcastMessage);

    return result;
};

export const initializeReduxBroadcastListener = (
    dispatch: (action: ReduxBroadcastMessage) => void,
): void => {
    const activeChannel = getChannel();
    if (!activeChannel || activeChannel.onmessage) return;

    activeChannel.onmessage = (event: MessageEvent<ReduxBroadcastMessage>) => {
        const message = event.data;
        if (!message || !reduxBroadcastActionTypeSet.has(message.type)) {
            return;
        }

        isRemoteDispatchInProgress = true;

        try {
            dispatch({
                type: message.type,
                payload: message.payload,
            });
        } finally {
            isRemoteDispatchInProgress = false;
        }
    };
};
